import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { useCardHoldingWallet } from "../../hooks/useCardHoldingWallet";
import { HoldingWalletPanel } from "./HoldingWalletPanel";
import { RechargeQuoteCalculator } from "./RechargeQuoteCalculator";

export interface RechargeQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Answers the one question a client asks before a recharge: how much to send, and how much of it lands.
 *
 * It is a reference tool, not a step of the adjustment: nothing here is submitted, and the credit is still
 * registered by hand with its own deposit reference once the money shows up. Hence the single Close action —
 * there is no outcome to confirm.
 *
 * It takes no organization, and that is the point. The pool it names belongs to Coloca and backs every
 * organization's cards at once, so the quote is the same whoever is on the other end of the call — which is
 * why the screen offers it above the organization picker rather than inside it.
 */
export function RechargeQuoteDialog({ open, onOpenChange }: RechargeQuoteDialogProps) {
  const { t } = useTranslations("cardWallets");
  // Read only once the dialog is actually open: it reaches the card provider live on every call.
  const pool = useCardHoldingWallet(open);

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      mobileFullscreen
      title={t("quote_title")}
      description={t("quote_description")}
      footer={
        <ColocaButton type="button" variant="primary" onClick={() => onOpenChange(false)}>
          {t("quote_close")}
        </ColocaButton>
      }
    >
      <div className="space-y-6">
        <HoldingWalletPanel
          wallet={pool.wallet}
          isLoading={pool.isLoading}
          error={pool.error}
          onRetry={pool.refresh}
        />
        <RechargeQuoteCalculator />
      </div>
    </ColocaDialog>
  );
}
